import { CheckCircle2, Copy, KeyRound, Server, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { PartnerShell } from './PartnerShell';

const apiBaseUrl = process.env.REACT_APP_PARTNER_API_BASE_URL || `${window.location.origin}/api`;

const commonErrors = [
  'MISSING_CLIENT_ID',
  'INVALID_CLIENT',
  'IP_NOT_ALLOWED',
  'MISSING_SIGNATURE_HEADERS',
  'INVALID_TIMESTAMP',
  'INVALID_SIGNATURE',
  'REPLAY_DETECTED',
  'MISSING_IDEMPOTENCY_KEY',
  'IDEMPOTENCY_CONFLICT',
  'FORBIDDEN',
  'EMAIL_REQUIRED',
  'MERCHANT_REF_EXISTS',
  'MERCHANT_EMAIL_EXISTS',
  'MERCHANT_BLACKLISTED',
  'MERCHANT_CREATE_FAILED',
  'MERCHANT_NOT_FOUND',
  'INVOICE_REF_EXISTS',
  'INVOICE_CREATE_FAILED',
  'INVOICE_NOT_FOUND',
  'VALIDATION_ERROR',
];

const goLiveItems = [
  'Receive client secret and callback secret from PayLink admin through an approved secure channel.',
  'Confirm production base URL and allowed source IP addresses.',
  'Generate a signed request and verify HMAC authentication succeeds.',
  'Create one test merchant with send_activation_email enabled.',
  'Create one invoice for that merchant and open the returned payment link.',
  'Query the invoice status for reconciliation.',
  'Receive and verify the signed invoice.paid callback.',
  'Store PayLink merchant and invoice identifiers for reconciliation.',
];

const CodeBlock = ({ children }) => (
  <pre className="overflow-x-auto rounded-lg bg-foreground p-4 text-sm text-background">
    <code>{children}</code>
  </pre>
);

const InfoRow = ({ label, value, mono = false }) => (
  <div className="min-w-0 rounded-lg border border-border bg-muted/30 p-4">
    <p className="text-xs font-medium uppercase text-muted-foreground">{label}</p>
    <p className={`mt-1 truncate text-sm font-semibold text-foreground ${mono ? 'font-mono' : ''}`}>
      {value || '-'}
    </p>
  </div>
);

const CopyButton = ({ value, label = 'Copy' }) => {
  const copy = async () => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  return (
    <Button variant="outline" size="sm" onClick={copy} disabled={!value}>
      <Copy className="mr-2 h-4 w-4" />
      {label}
    </Button>
  );
};

const PartnerDocs = () => {
  const { user } = useAuth();
  const partner = user?.partner || {};
  const clientId = partner.clientId || '';
  const allowedIps = Array.isArray(partner.allowedIps) && partner.allowedIps.length > 0
    ? partner.allowedIps.join(', ')
    : 'Not configured';

  const signaturePayload = `POST
/api/partner/invoices
1783411200
nonce-8f3f2f1a
{"partner_merchant_ref":"M-10001","amount":50000}`;

  const merchantCurl = `curl -X POST "${apiBaseUrl}/partner/merchants" \\
  -H "Content-Type: application/json" \\
  -H "X-Client-Id: ${clientId || '<partner-client-id>'}" \\
  -H "X-Timestamp: <utc-unix-timestamp-seconds>" \\
  -H "X-Nonce: <unique-random-string>" \\
  -H "X-Signature: <hmac-sha256-signature>" \\
  -H "Idempotency-Key: merchant-M-10001" \\
  -d '{
    "partner_merchant_ref": "M-10001",
    "merchant_type": "COMPANY",
    "business_name": "Example Store LLC",
    "currency": "MNT",
    "email": "merchant@example.mn",
    "phone": "99112233",
    "owner": {
      "first_name": "Bat",
      "last_name": "Bold",
      "register_no": "UA00112233"
    },
    "settlement_account": {
      "bank_code": "050000",
      "account_number": "5000000000"
    },
    "send_activation_email": true
  }'`;

  const invoiceCurl = `curl -X POST "${apiBaseUrl}/partner/invoices" \\
  -H "Content-Type: application/json" \\
  -H "X-Client-Id: ${clientId || '<partner-client-id>'}" \\
  -H "X-Timestamp: <utc-unix-timestamp-seconds>" \\
  -H "X-Nonce: <unique-random-string>" \\
  -H "X-Signature: <hmac-sha256-signature>" \\
  -H "Idempotency-Key: invoice-ORDER-10001" \\
  -d '{
    "partner_merchant_ref": "M-10001",
    "partner_invoice_ref": "ORDER-10001",
    "amount": 50000,
    "currency": "MNT",
    "description": "Order #10001",
    "callback_url": "https://partner.example.mn/paylink/callback",
    "timeout": 60
  }'`;

  const invoiceStatusCurl = `curl -X GET "${apiBaseUrl}/partner/invoices/ORDER-10001" \\
  -H "X-Client-Id: ${clientId || '<partner-client-id>'}" \\
  -H "X-Timestamp: <utc-unix-timestamp-seconds>" \\
  -H "X-Nonce: <unique-random-string>" \\
  -H "X-Signature: <hmac-sha256-signature>"`;

  return (
    <PartnerShell>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-medium text-primary">API Documentation</p>
            <h1 className="text-2xl font-bold text-foreground md:text-3xl">PayLink Partner API</h1>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              Register verified merchants, create invoices, and receive paid callbacks from PayLink.
            </p>
          </div>
          <CopyButton value={clientId} label="Client ID" />
        </div>

        <section className="space-y-4 rounded-lg border border-border bg-white p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <KeyRound className="h-4 w-4 text-primary" />
            Partner Access
          </div>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <InfoRow label="Client ID" value={clientId} mono />
            <InfoRow label="Partner Status" value={Number(partner.status) === 1 ? 'Active' : 'Inactive'} />
            <InfoRow label="Merchant API" value={partner.canOnboardMerchant ? 'Enabled' : 'Disabled'} />
            <InfoRow label="Invoice API" value={partner.canCreateInvoice ? 'Enabled' : 'Disabled'} />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <InfoRow label="Allowed Source IPs" value={allowedIps} mono />
            <InfoRow label="Callback Secret" value={partner.callbackSecretConfigured ? 'Configured' : 'Not configured'} />
          </div>
          <p className="text-sm text-muted-foreground">
            Client secret and callback secret are provided once by PayLink admin through an approved secure channel. They are not displayed here after provisioning.
          </p>
          <p className="text-sm text-muted-foreground">
            Source IP allowlist is required. Use exact IPs, IPv4 CIDR ranges, or * only when PayLink admin intentionally allows every source IP.
          </p>
        </section>

        <section className="space-y-4 rounded-lg border border-border bg-white p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Server className="h-4 w-4 text-primary" />
            Base URL and Headers
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <InfoRow label="Base URL" value={apiBaseUrl} mono />
            <CopyButton value={apiBaseUrl} label="Base URL" />
          </div>
          <CodeBlock>{`X-Client-Id: <partner-client-id>
X-Timestamp: <utc-unix-timestamp-seconds>
X-Nonce: <unique-random-string>
X-Signature: <hmac-sha256-signature>
Idempotency-Key: <unique-operation-key>
Content-Type: application/json`}</CodeBlock>
        </section>

        <section className="space-y-4 rounded-lg border border-border bg-white p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <ShieldCheck className="h-4 w-4 text-primary" />
            HMAC Signature
          </div>
          <p className="text-sm text-muted-foreground">
            Sign the exact raw JSON body you send. The signature is lowercase hex HMAC-SHA256 using your Partner client secret.
          </p>
          <CodeBlock>{`<HTTP_METHOD>
<REQUEST_PATH>
<X-Timestamp>
<X-Nonce>
<RAW_REQUEST_BODY>`}</CodeBlock>
          <CodeBlock>{signaturePayload}</CodeBlock>
        </section>

        <section className="space-y-4 rounded-lg border border-border bg-white p-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-foreground">Create Merchant</h2>
            <CopyButton value={merchantCurl} label="cURL" />
          </div>
          <CodeBlock>{merchantCurl}</CodeBlock>
          <CodeBlock>{`{
  "success": true,
  "merchant": {
    "id": 123,
    "partner_merchant_ref": "M-10001",
    "status": "active",
    "business_name": "Example Store LLC",
    "dashboard_user_created": true,
    "activation_email_sent": true
  }
}`}</CodeBlock>
        </section>

        <section className="space-y-4 rounded-lg border border-border bg-white p-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-foreground">Create Invoice</h2>
            <CopyButton value={invoiceCurl} label="cURL" />
          </div>
          <CodeBlock>{invoiceCurl}</CodeBlock>
          <CodeBlock>{`{
  "success": true,
  "invoice": {
    "id": "inv_xxxxx",
    "partner_invoice_ref": "ORDER-10001",
    "status": "pending",
    "amount": 50000,
    "currency": "MNT",
    "payment_link": "https://paylink.mn/link/inv_xxxxx",
    "merchant_ref": "ORDER-10001",
    "callback_status": "pending",
    "created_at": "2026-07-07T10:00:00+08:00",
    "expire_at": "2026-07-07T11:00:00+08:00",
    "paid_at": null
  }
}`}</CodeBlock>
        </section>

        <section className="space-y-4 rounded-lg border border-border bg-white p-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-foreground">Get Invoice Status</h2>
            <CopyButton value={invoiceStatusCurl} label="cURL" />
          </div>
          <p className="text-sm text-muted-foreground">
            Query by partner_invoice_ref or PayLink invoice id. Idempotency-Key is not required for this read-only request.
          </p>
          <CodeBlock>{invoiceStatusCurl}</CodeBlock>
          <CodeBlock>{`{
  "success": true,
  "invoice": {
    "id": "inv_xxxxx",
    "partner_invoice_ref": "ORDER-10001",
    "status": "paid",
    "amount": 50000,
    "currency": "MNT",
    "payment_link": "https://paylink.mn/link/inv_xxxxx",
    "merchant_ref": "ORDER-10001",
    "callback_status": "sent",
    "created_at": "2026-07-07T10:00:00+08:00",
    "expire_at": "2026-07-07T11:00:00+08:00",
    "paid_at": "2026-07-07T10:05:00+08:00"
  }
}`}</CodeBlock>
        </section>

        <section className="space-y-4 rounded-lg border border-border bg-white p-5">
          <h2 className="text-lg font-semibold text-foreground">Payment Callback</h2>
          <p className="text-sm text-muted-foreground">
            PayLink sends a signed callback to the invoice callback_url when the invoice becomes paid. Pending or failed callbacks are retried by the PayLink callback processor.
          </p>
          <CodeBlock>{`X-PayLink-Timestamp: <unix-timestamp-seconds>
X-PayLink-Nonce: <unique-random-string>
X-PayLink-Signature: <hmac-sha256-signature>
Content-Type: application/json`}</CodeBlock>
          <CodeBlock>{`{
  "event": "invoice.paid",
  "invoice_id": "inv_xxxxx",
  "partner_invoice_ref": "ORDER-10001",
  "partner_merchant_ref": "M-10001",
  "status": "paid",
  "amount": 50000,
  "currency": "MNT",
  "paid_at": "2026-07-07T10:05:00+08:00"
}`}</CodeBlock>
        </section>

        <section className="space-y-4 rounded-lg border border-border bg-white p-5">
          <h2 className="text-lg font-semibold text-foreground">Error Codes</h2>
          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
            {commonErrors.map((code) => (
              <div key={code} className="rounded-md border border-border bg-muted/30 px-3 py-2 font-mono text-xs text-foreground">
                {code}
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-4 rounded-lg border border-border bg-white p-5">
          <h2 className="text-lg font-semibold text-foreground">Go-Live Checklist</h2>
          <div className="space-y-3">
            {goLiveItems.map((item) => (
              <div key={item} className="flex gap-3 text-sm text-foreground">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-success" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </PartnerShell>
  );
};

export default PartnerDocs;
